/**
 * Text Generate Effect Component
 * 
 * A component that creates an animated text effect where words appear sequentially
 * with a blur and slide animation. Supports single or double line text with
 * independent styling for each line.
 * 
 * Features:
 * - Sequential word animation
 * - Blur and slide effects
 * - Customizable timing
 * - Support for two lines of text
 * - Smooth transitions
 */

'use client';
import { useEffect, MutableRefObject, useCallback, useState } from 'react';
import { motion, stagger, useAnimate } from 'framer-motion';
import { cn } from '@/utils/utils';

/**
 * Props interface for the TextGenerateEffect component
 * @interface TextGenerateEffectProps
 * @property {string} words - The main text to animate
 * @property {string} [className] - Additional CSS classes for the main text
 * @property {boolean} [filter=true] - Whether to apply blur filter during animation
 * @property {number} [duration=0.5] - Duration of each word's animation in seconds
 * @property {string} [secondLineWords] - Optional second line of text to animate
 * @property {string} [secondLineClassName] - Additional CSS classes for the second line
 */
export const TextGenerateEffect = ({
	words,
	className,
	filter = true,
	duration = 0.5,
	secondLineWords,
	secondLineClassName,
}: {
	words: string;
	className?: string;
	filter?: boolean;
	duration?: number;
	secondLineWords?: string;
	secondLineClassName?: string;
}) => {
	const [scope1, animate1] = useAnimate();
	const [scope2, animate2] = useAnimate();
	const [isMounted, setIsMounted] = useState(false);
	const [elementsReady, setElementsReady] = useState(false);
	
	let wordsArray = words.split(' ');
	let secondLineArray = secondLineWords ? secondLineWords.split(' ') : [];

	// Calculate timing for smoother transitions
	const staggerDelay = 0.12; // Reduced for smoother flow
	const firstRowTotalDuration = staggerDelay * (wordsArray.length - 1);

	/**
	 * Animates the text with sequential word appearances
	 * @async
	 */
	const animateText = useCallback(async () => {
		if (!isMounted || !elementsReady) return;

		// First line animation
		await animate1(
			'span',
			{
				opacity: 1,
				filter: filter ? 'blur(0px)' : 'none',
				transform: 'translateY(0)',
			},
			{
				duration: duration,
				delay: stagger(staggerDelay),
				ease: [0.2, 0.65, 0.3, 0.9], // Custom easing for smoother motion
			},
		);

		// Second line animation after first line completes
		if (secondLineArray.length > 0) {
			setTimeout(() => {
				animate2(
					'span',
					{
						opacity: 1,
						filter: filter ? 'blur(0px)' : 'none',
						transform: 'translateY(0)',
					},
					{
						duration: duration,
						delay: stagger(staggerDelay),
						ease: [0.2, 0.65, 0.3, 0.9], // Custom easing for smoother motion
					},
				);
			}, firstRowTotalDuration * 1000);
		}
	}, [animate1, animate2, duration, filter, firstRowTotalDuration, secondLineArray.length, staggerDelay, isMounted, elementsReady]);

	/**
	 * Sets mounted state when component is mounted
	 */
	useEffect(() => {
		setIsMounted(true);
	}, []);

	/**
	 * Checks if all required elements are available in the DOM
	 */
	useEffect(() => {
		if (isMounted) {
			// Check if elements are available
			const checkElements = () => {
				const elements1 = scope1.current?.querySelectorAll('span');
				const elements2 = scope2.current?.querySelectorAll('span');
				if (elements1?.length && (!secondLineArray.length || elements2?.length)) {
					setElementsReady(true);
				} else {
					requestAnimationFrame(checkElements);
				}
			};
			checkElements();
		}
	}, [isMounted, scope1, scope2, secondLineArray.length]);

	/**
	 * Triggers animation when elements are ready
	 */
	useEffect(() => {
		if (isMounted && elementsReady) {
			animateText();
		}
	}, [animateText, isMounted, elementsReady]);

	/**
	 * Renders animated words
	 * @param {string[]} words - Array of words to render
	 * @param {MutableRefObject<HTMLDivElement | null>} scope - Reference to the container
	 * @param {string} [className] - Additional CSS classes
	 * @returns {JSX.Element} Rendered words with animation
	 */
	const renderWords = (words: string[], scope: MutableRefObject<HTMLDivElement | null>, className?: string) => {
		return (
			<motion.div ref={scope} className={className}>
				{words.map((word, idx) => (
					<motion.span
						key={`${word}-${idx}`}
						className="inline-block mr-[0.25em]"
						initial={{
							opacity: 0,
							filter: filter ? 'blur(4px)' : 'none',
							transform: 'translateY(4px)',
						}}
						style={{
							willChange: 'transform, opacity, filter',
							backfaceVisibility: 'hidden',
							WebkitFontSmoothing: 'antialiased',
						}}
					>
						{word}
					</motion.span>
				))}
			</motion.div>
		);
	};

	return (
		<div className={cn('font-bold', className)}>
			{renderWords(wordsArray, scope1)}
			{secondLineArray.length > 0 && renderWords(secondLineArray, scope2, secondLineClassName)}
		</div>
	);
};
